import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Trophy, ListChecks, BarChart3, Settings, LogOut, User, Swords, Newspaper } from "lucide-react";
import InstallButton from "@/components/InstallButton";

const linkBase =
  "flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.15em] font-semibold transition-colors duration-200 border-b-2";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const nav = ({ isActive }) =>
    `${linkBase} ${
      isActive
        ? "text-white border-[#E31837]"
        : "text-white/50 border-transparent hover:text-white hover:border-white/30"
    }`;

  return (
    <header className="sticky top-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-16">
        <div className="flex items-center gap-8">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate("/standings")}
            data-testid="nav-logo"
          >
            <div className="w-8 h-8 bg-[#E31837] flex items-center justify-center">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <span className="font-['Bebas_Neue'] text-2xl tracking-wide leading-none">
              PICK'EM POOL
            </span>
          </div>
          <nav className="hidden md:flex items-center h-16">
            <NavLink to="/picks" className={nav} data-testid="nav-picks">
              <ListChecks className="w-4 h-4" /> Picks
            </NavLink>
            <NavLink to="/results" className={nav} data-testid="nav-results">
              <BarChart3 className="w-4 h-4" /> Results
            </NavLink>
            <NavLink to="/standings" className={nav} data-testid="nav-standings">
              <Trophy className="w-4 h-4" /> Standings
            </NavLink>
            <NavLink to="/head-to-head" className={nav} data-testid="nav-h2h">
              <Swords className="w-4 h-4" /> H2H
            </NavLink>
            <NavLink to="/news" className={nav} data-testid="nav-news">
              <Newspaper className="w-4 h-4" /> News
            </NavLink>
            <NavLink to={`/profile/${user?.username}`} className={nav} data-testid="nav-profile">
              <User className="w-4 h-4" /> My Profile
            </NavLink>
            {user?.role === "admin" && (
              <NavLink to="/admin" className={nav} data-testid="nav-admin">
                <Settings className="w-4 h-4" /> Admin
              </NavLink>
            )}
          </nav>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-white/70 hidden sm:block" data-testid="nav-username">
            {user?.username}
            {user?.role === "admin" && (
              <span className="ml-2 text-[10px] bg-[#E31837] px-1.5 py-0.5 uppercase tracking-wider">
                Commish
              </span>
            )}
          </span>
          <InstallButton />
          <button
            onClick={logout}
            className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-white/60 hover:text-[#E31837] transition-colors duration-200"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" /> Exit
          </button>
        </div>
      </div>
      {/* mobile nav */}
      <nav className="md:hidden flex items-center border-t border-white/10 overflow-x-auto">
        <NavLink to="/picks" className={nav} data-testid="nav-picks-m">Picks</NavLink>
        <NavLink to="/results" className={nav} data-testid="nav-results-m">Results</NavLink>
        <NavLink to="/standings" className={nav} data-testid="nav-standings-m">Standings</NavLink>
        <NavLink to="/head-to-head" className={nav} data-testid="nav-h2h-m">H2H</NavLink>
        <NavLink to="/news" className={nav} data-testid="nav-news-m">News</NavLink>
        <NavLink to={`/profile/${user?.username}`} className={nav} data-testid="nav-profile-m">Profile</NavLink>
        {user?.role === "admin" && (
          <NavLink to="/admin" className={nav} data-testid="nav-admin-m">Admin</NavLink>
        )}
      </nav>
    </header>
  );
}
